// frontend/app/components/KnowledgeCenterModal.js
// Knowledge Center — a standalone tab that guides users through each app
// functionality. The content is sourced from i18n/glossary.js (the same
// single source of truth used by the Support modal's "What's the difference?"
// tab), so both surfaces stay in sync. Built as an expandable accordion so
// more per-feature guidance can be added over time.
import React, { useMemo, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  GLOSSARY_KINDS,
  getGlossaryEntry,
  getGlossaryAccentColor,
} from "../i18n/glossary";

export default function KnowledgeCenterModal({ visible, onClose, isDarkMode, t }) {
  const styles = useMemo(() => makeStyles(isDarkMode), [isDarkMode]);
  const translate = typeof t === "function" ? t : (s) => s;

  // Which topic card is expanded. The first topic starts open so the panel
  // never looks empty; tapping a header toggles it.
  const [expandedKind, setExpandedKind] = useState(GLOSSARY_KINDS[0] || null);

  const closeAll = () => onClose?.();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={closeAll}>
      <View style={styles.backdrop}>
        <View style={styles.modalCard}>
          {/* Header */}
          <View style={styles.topBar}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <MaterialCommunityIcons
                name="book-open-page-variant"
                size={20}
                color={styles._c.text}
              />
              <Text style={styles.topTitle}>{translate("App Guide") || "App Guide"}</Text>
            </View>

            <TouchableOpacity onPress={closeAll} style={styles.closeBtn}>
              <MaterialCommunityIcons name="close" size={20} color={styles._c.text} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.panel} contentContainerStyle={{ paddingBottom: 12 }}>
            <Text style={styles.intro}>
              {translate("Learn what each part of the app does and when to use it.") ||
                "Learn what each part of the app does and when to use it."}
            </Text>

            {GLOSSARY_KINDS.map((kind) => {
              const entry = getGlossaryEntry(kind);
              if (!entry) return null;
              const accent =
                getGlossaryAccentColor(entry.accent, isDarkMode) || styles._c.primary;
              const expanded = expandedKind === kind;
              return (
                <View key={kind} style={[styles.card, { borderColor: styles._c.border }]}>
                  <TouchableOpacity
                    onPress={() => setExpandedKind(expanded ? null : kind)}
                    style={styles.cardHeader}
                    activeOpacity={0.7}
                  >
                    <View
                      style={[
                        styles.iconChip,
                        { backgroundColor: `${accent}22`, borderColor: accent },
                      ]}
                    >
                      <MaterialCommunityIcons name={entry.iconName} size={22} color={accent} />
                    </View>
                    <Text style={[styles.cardLabel, { color: styles._c.text }]}>
                      {translate(entry.labelKey) || entry.labelKey}
                    </Text>
                    <MaterialCommunityIcons
                      name={expanded ? "chevron-up" : "chevron-down"}
                      size={22}
                      color={styles._c.muted}
                    />
                  </TouchableOpacity>

                  {expanded && (
                    <Text style={[styles.cardDefinition, { color: styles._c.muted }]}>
                      {translate(entry.definitionKey) || entry.definitionKey}
                    </Text>
                  )}
                </View>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function makeStyles(isDark) {
  const c = {
    bg: isDark ? "#0b1220" : "#f3f4f6",
    card: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "#22324a" : "#e5e7eb",
    text: isDark ? "#e5e7eb" : "#111827",
    muted: isDark ? "#9ca3af" : "#6b7280",
    primary: "#2563eb",
  };

  const s = StyleSheet.create({
    _c: c,
    backdrop: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.55)",
      justifyContent: "center",
      padding: 16,
    },
    modalCard: {
      backgroundColor: c.card,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: c.border,
      overflow: "hidden",
      maxHeight: "90%",
      maxWidth: 560,
      width: "100%",
      alignSelf: "center",
    },

    topBar: {
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: c.bg,
    },
    topTitle: { color: c.text, fontWeight: "700", marginLeft: 8, fontSize: 16 },

    closeBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.border,
      alignItems: "center",
      justifyContent: "center",
    },

    panel: { padding: 12 },

    intro: {
      color: c.muted,
      fontSize: 13,
      lineHeight: 18,
      marginBottom: 12,
    },

    card: {
      borderRadius: 12,
      borderWidth: 1,
      marginBottom: 10,
      backgroundColor: isDark ? "#0b1220" : "#f9fafb",
      overflow: "hidden",
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      padding: 12,
    },
    iconChip: {
      width: 40,
      height: 40,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
    },
    cardLabel: {
      flex: 1,
      fontWeight: "800",
      fontSize: 15,
    },
    cardDefinition: {
      fontSize: 13,
      lineHeight: 18,
      paddingHorizontal: 12,
      paddingBottom: 12,
    },
  });

  s._c = c;
  return s;
}
